// services/gsapAnimations.js
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

/**
 * GSAP Animation Service
 * Reusable animation functions for transitions and product lists
 */
export const gsapAnimations={
  /**
   * Fade-in animation for page transitions
   * @param {HTMLElement} element - The wrapper element to animate
   * @param {Object} options - Animation options
   * @param {number} [options.duration=0.6] - Duration in seconds
   * @param {string} [options.ease="power1.out"] - GSAP easing type
   */
  animatePageChange1(element, options={}) {
    if (!element) {
      console.warn("animatePageChange: element is null");
      return;
    }
    const {
      duration=0.6,
      ease="power1.out"
    }=options;

    gsap.fromTo(
      element,
      { opacity: 0 },
      { opacity: 1, duration, ease }
    );
  },
  animatePageChange(element, options={}) {
    if (!element) return;

    const {
      duration=0.6,
      ease="power2.out",
      direction="down" // left, right, up, down
    }=options;

    // choose margin property
    let from={};
    let to={};

    if (direction==="right") {
      from={ opacity: 1, marginLeft: "30px" };
      to={ opacity: 1, marginLeft: "0px" };
    } else if (direction==="left") {
      from={ opacity: 1, marginLeft: "-30px" };
      to={ opacity: 1, marginLeft: "0px" };
    } else if (direction==="up") {
      from={ opacity: 1, marginTop: "-30px" };
      to={ opacity: 1, marginTop: "0px" };
    } else if (direction==="down") {
      from={ opacity: 1, marginTop: "30px" };
      to={ opacity: 1, marginTop: "0px" };
    }

    gsap.fromTo(element, from, { ...to, duration, ease });
  }

  ,
  /**
   * Setup scroll-triggered animation for product items
   */
  setupScrollAnimation(triggerElement, itemSelector, options={}) {
    if (!triggerElement) {
      console.warn("setupScrollAnimation: triggerElement is null");
      return;
    }

    const items=triggerElement.querySelectorAll(itemSelector);
    if (!items||items.length===0) {
      console.warn(`setupScrollAnimation: No items found with selector "${itemSelector}"`);
      return;
    }

    const {
      start="top 80%",
      duration=0.25,
      stagger=0.15,
      yOffset=80,
    }=options;

    gsap.set(items, { opacity: 0.5, y: yOffset });

    ScrollTrigger.create({
      trigger: triggerElement,
      start,
      once: true,
      onEnter: () => {
        gsap.to(items, {
          opacity: 1,
          y: 0,
          duration,
          ease: "power2.out",
          stagger,
        });
      },
    });
  },

  animateNewItems(containerElement, itemSelector, options={}) {
    if (!containerElement) {
      console.warn("animateNewItems: containerElement is null");
      return;
    }

    const items=containerElement.querySelectorAll(itemSelector);
    if (!items||items.length===0) {
      console.warn(`animateNewItems: No items found with selector "${itemSelector}"`);
      return;
    }

    const {
      duration=0.5,
      stagger=0.15,
      yOffset=40,
    }=options;

    gsap.fromTo(
      items,
      { opacity: 0, y: yOffset },
      {
        opacity: 1,
        y: 0,
        duration,
        ease: "power2.out",
        stagger,
      }
    );
  },

  animateItemRange(containerElement, itemSelector, startIndex, endIndex, options={}) {
    if (!containerElement) {
      console.warn("animateItemRange: containerElement is null");
      return;
    }

    const items=containerElement.querySelectorAll(itemSelector);
    if (!items||items.length===0) {
      console.warn(`animateItemRange: No items found with selector "${itemSelector}"`);
      return;
    }

    const {
      duration=0.5,
      stagger=0.15,
      yOffset=40,
    }=options;

    const itemsToAnimate=Array.from(items).slice(startIndex, endIndex);

    gsap.fromTo(
      itemsToAnimate,
      { opacity: 0, y: yOffset },
      {
        opacity: 1,
        y: 0,
        duration,
        ease: "power2.out",
        stagger,
      }
    );
  },

  killAllScrollTriggers() {
    ScrollTrigger.getAll().forEach(trigger => trigger.kill());
  },

  refreshScrollTriggers() {
    ScrollTrigger.refresh();
  },
};

export default gsapAnimations;
